/**
 * Catchball Path Instance store — planned spine vs actual/adjustable walk.
 * Canon 29 §2.2. Mutations via RPCs (migration 617).
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../../lib/supabaseSync';
import {
  type CatchballPathInstance,
  type EnsurePathInstanceParams,
  type AdjustPathInstanceParams,
  rowToPathInstance,
} from '../../types/prioritization/catchballPathInstance';

interface CatchballPathInstanceState {
  byLoop: Record<string, CatchballPathInstance | null>;
  loading: Record<string, boolean>;
  error: Record<string, string | null>;

  getForLoop: (loopId: string) => CatchballPathInstance | null;
  fetchForLoop: (loopId: string) => Promise<CatchballPathInstance | null>;
  ensurePathInstance: (params: EnsurePathInstanceParams) => Promise<{
    success: boolean;
    error?: string;
    pathInstance?: CatchballPathInstance;
  }>;
  adjustPathInstance: (params: AdjustPathInstanceParams) => Promise<{
    success: boolean;
    error?: string;
    pathInstance?: CatchballPathInstance;
  }>;
}

export const useCatchballPathInstanceStore = create<CatchballPathInstanceState>((set, get) => ({
  byLoop: {},
  loading: {},
  error: {},

  getForLoop: (loopId) => get().byLoop[loopId] ?? null,

  fetchForLoop: async (loopId) => {
    if (!isSupabaseAvailable()) return null;
    set((s) => ({ loading: { ...s.loading, [loopId]: true }, error: { ...s.error, [loopId]: null } }));
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      const { data, error } = await supabase
        .from('catchball_path_instances')
        .select('*')
        .eq('loop_id', loopId)
        .maybeSingle();
      if (error) throw error;
      const pi = data ? rowToPathInstance(data as Record<string, unknown>) : null;
      set((s) => ({
        byLoop: { ...s.byLoop, [loopId]: pi },
        loading: { ...s.loading, [loopId]: false },
      }));
      return pi;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load path instance';
      set((s) => ({
        loading: { ...s.loading, [loopId]: false },
        error: { ...s.error, [loopId]: msg },
      }));
      return null;
    }
  },

  ensurePathInstance: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_ensure_path_instance', {
      p_loop_id: params.loopId,
      p_spine_org_unit_ids: params.spineOrgUnitIds,
      p_path_source: params.pathSource ?? 'seeded',
      p_actor_id: params.actorId,
    });
    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      path_instance?: Record<string, unknown>;
    };
    if (!result.success) return { success: false, error: result.error };

    const pi = result.path_instance
      ? rowToPathInstance(result.path_instance)
      : await get().fetchForLoop(params.loopId);
    if (pi) set((s) => ({ byLoop: { ...s.byLoop, [params.loopId]: pi } }));
    return { success: true, pathInstance: pi ?? undefined };
  },

  adjustPathInstance: async (params) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Database unavailable' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const { data, error } = await supabase.rpc('catchball_adjust_path_instance', {
      p_loop_id: params.loopId,
      p_ball_id: params.ballId ?? null,
      p_remaining_org_unit_ids: params.remainingOrgUnitIds,
      p_mode: params.mode ?? 'rewrite_remaining',
      p_actor_id: params.actorId,
    });
    if (error) return { success: false, error: error.message };

    const result = data as {
      success: boolean;
      error?: string;
      path_instance?: Record<string, unknown>;
    };
    if (!result.success) return { success: false, error: result.error };

    const pi = result.path_instance
      ? rowToPathInstance(result.path_instance)
      : await get().fetchForLoop(params.loopId);
    if (pi) set((s) => ({ byLoop: { ...s.byLoop, [params.loopId]: pi } }));
    return { success: true, pathInstance: pi ?? undefined };
  },
}));
